import { Component, Input, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { IconType, IoniconsOutline, LoggedUser, ND, PtPermissions } from '@pt/pt-models';
import { Observable, of, Subject } from 'rxjs';
import { PtCoreSelectors } from '@pt/pt-core';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { EChartsOption } from 'echarts';
import { TranslateService } from '@ngx-translate/core';
import { DpiaEvaluationMap } from '@primatoo-ms-organization';
import { TreatmentService } from '@pt/api-client';

/**
 * ATTENZIONE: questo componente è usato anche all'interno dell'editor della DPIA,
 * Li il suo funzionamento è corretto, se utilizzato altrove o clonarlo o evitare di modificare il comportamento nell'editor
 * nota di @giovanni
 */
@Component({
  selector: 'pt-dpia-evaluation-map',
  template: `

    <div [ngClass]='containerCssClass'>
      <button class='btn btn-sm btn-outline-primary w-100' (click)='open(content)'>
        <pt-icon [key]='IconStatsComment' [iconType]='IconType.IONIC_ICON' [size]='14'></pt-icon>
        <span>{{ 'TITLE.CROSSING_MATRIX' | translate }}
          </span>
      </button>
    </div>

    <ng-template #content>
      <div class='modal-header'>
        <h4 class='modal-title' id='modal-basic-title'>
          {{ title | async }}
        </h4>
        <button type='button' class='btn-close' aria-label='Close' (click)='closeDialog()'>
        </button>
      </div>
      <div class='modal-body modal-body-dpia-evaluation-map'>

        <div *ngIf='showError'
             class='h-100 w-100 d-flex flex-column justify-content-center align-items-center'>
          <div class='text-center'>
            <pt-icon class='text-warning' [key]='IconWarning' [size]='30'></pt-icon>
            <p>{{ 'MESSAGE.NO_CROSSING_MATRIX' | translate }}</p>
          </div>
        </div>

        <div *ngIf='!showError'
             echarts
             [options]='chartOption'
             class="dpia-chart">
        </div>

      </div>
    </ng-template>
  `,
  styles: [`
    .modal-body-dpia-evaluation-map {
      padding: 0;
      overflow-y: auto;
      height: 300px;
    }

    .dpia-chart {
      height: 100%;
      width: 100%;
    }

  `]
})
export class DpiaEvaluationMapComponent implements OnDestroy {

  @Input() containerCssClass: string;

  public chartOption: EChartsOption;

  public title: Observable<string>;

  public loginResponse$: Observable<LoggedUser> = this.store.select(PtCoreSelectors.loginResponse);

  public IconType = IconType;
  public IconStatsComment = IoniconsOutline.statsChart;
  public IconWarning = IoniconsOutline.warning;
  public dpiaEvalutationMap: DpiaEvaluationMap;

  public PtPermissions = PtPermissions;
  public showError = false;
  private currentDialog;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private treatmentService: TreatmentService,
    private translateService: TranslateService,
    private store: Store,
    private modalService: NgbModal
  ) {
  }

  open(content) {
    this.loadDpiaEvalutationMap(content);
  }

  ngOnDestroy(): void {
    this.closeDialog();
    this.destroy$.next(true);
  }

  closeDialog() {
    if (this.currentDialog) {
      this.currentDialog.close();
    }
  }

  private loadDpiaEvalutationMap(content) {
    this.treatmentService.getDpiaEvalutationMap().pipe(
      takeUntil(this.destroy$)
    ).subscribe((response) => {
      this.showError = false;
      this.dpiaEvalutationMap = response;
      this.createOption(this.dpiaEvalutationMap);
      this.currentDialog = this.modalService.open(content, {
        centered: true,
        keyboard: false,
        backdrop: 'static',
        size: '500px'
      });
    }, () => {
      this.showError = true;
    });
  }

  private isValidResponse(response: DpiaEvaluationMap): boolean {
    return response?.value &&
      response?.labels?.length === 5 &&
      response?.title &&
      response?.colors.length === 5;
  }

  private createOption(response: DpiaEvaluationMap) {
    if (this.isValidResponse(response)) {
      this.title = of(response.title);
      const xAxisData = [1, 2, 3, 4, 5];
      const yAxisData = [ 1, 2, 3, 4, 5];
      const labels = response.labels;
      const data = [];
      response.value.forEach((row, indexRow) => {
        row.forEach((col, indexCol) => {
          data.push([indexRow, indexCol, row[indexCol]]);
        })
      })
      this.chartOption = {
        tooltip: {
          formatter: function (params) {
            console.log(params.data[2]);
            return labels[`${params.data[2] - 1}`];
          },
        },
        grid: {
          top: 40,
          right: 130,
          left: 40,
          bottom: 50
        },
        xAxis: {
          name: response.xlabel,
          type: 'category',
          data: xAxisData,
          splitArea: {
            show: true
          }
        },
        yAxis: {
          name: response.ylabel,
          type: 'category',
          data: yAxisData,
          splitArea: {
            show: true
          }
        },
        visualMap: {
          type: 'piecewise',
          min: 0,
          max: 5,
          calculable: true,
          right: 10,
          top: 'center',
          realtime: false,
          splitNumber: 5,
          formatter: function (min, max) {
            console.log('min ', min);
            console.log('max ', max);
            const labelIndex = Number.parseInt(max?.toString());
            if (labelIndex > 0 && labelIndex <= labels?.length) {
              return labels[labelIndex - 1 ];
            }
            return ND;
          },
          inRange: {
            color: response.colors
          }
        },
        series: [
          {
            name: response.title,
            type: 'heatmap',
            data: data,
            label: {
              show: true
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }
        ]
      };
      this.showError = false;
    } else {
      this.title = this.translateService.stream('TITLE.CROSSING_MATRIX');
      this.showError = true;
    }
  }
}
